import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { getAdminWhatsNewVersions, setAdminWhatsNewPinned } from "@/api/admin-api"
import { queryKeys } from "@/lib/query-keys"

export function useAdminWhatsNewVersionsQuery() {
  return useQuery({
    queryKey: queryKeys.adminWhatsNewVersions,
    queryFn: () => getAdminWhatsNewVersions(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Manual refresh that bypasses both the React Query cache and the 60s
 * server-side `_list_versioned_blobs` cache. Wire to a refresh button so
 * admins can verify override / pin / blob changes immediately after editing
 * the manifest in Azure Portal — no waiting for the TTL.
 */
export function useRefreshAdminWhatsNewMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => getAdminWhatsNewVersions({ force: true }),
    onSuccess: (data) => {
      // Seed the cache with the fresh response instead of triggering a
      // second round-trip via invalidateQueries.
      queryClient.setQueryData(queryKeys.adminWhatsNewVersions, data)
    },
  })
}

export function useSetAdminWhatsNewPinnedMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (version: string | null) => setAdminWhatsNewPinned(version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWhatsNewVersions })
      // Invalidate every cached locale of the user-facing What's New so the
      // dialog reflects the new effective version on next mount.
      void queryClient.invalidateQueries({ queryKey: ["whats-new"] })
    },
  })
}
